import { sectionRenderer } from '@theme/section-renderer';

/**
 * Cart restore sync (Made in Matcha).
 *
 * THE BUG. On mobile, Back from checkout showed a cart drawer with lines the customer had
 * already removed, under a header bubble with the right count. The server cart was right
 * (checkout, abandoned-cart email and other devices all agreed). A reload fixed it.
 *
 * WHY. On Back, the browser can restore the page from a snapshot instead of rendering it
 * fresh. There are two ways it does this:
 *   - the back/forward cache, which revives the old document (`pageshow.persisted === true`);
 *   - the HTTP cache, which builds a new document from the old HTML (navigation type
 *     `back_forward`).
 * Mobile browsers take these paths most readily, but the HTTP-cache path also reproduces on
 * desktop Chromium (verified 2026-09-23: clear lines in another page, go Back, drawer shows
 * the old lines and navigation type is `back_forward`). Either way, the drawer
 * shows lines that were server-rendered into that old HTML. Horizon patches the header bubble
 * on restore (assets/cart-icon.js, from a sessionStorage count and only if it's under 10s old).
 * The drawer itself (assets/component-cart-items.js) has no restore path. It only re-renders
 * on `cart:lines-update`.
 *
 * THE FIX. On any back/forward restore, re-render every cart-items section from the server
 * and set the bubble from /cart.js. A normal page load does nothing. The re-render is the same
 * `renderSection` call vendor makes for external cart updates, so rows, totals, the hidden
 * count and the free-shipping bar (hydration keys) all come from the server.
 *
 * Deliberately NOT a synthetic `CartLinesUpdateEvent`: that is a Shopify standard event, and
 * analytics or apps listening for it would log a cart change that never happened.
 *
 * Loaded globally from snippets/custom.globals.liquid — zero vendor edit.
 */

/** True when this document was created by a back/forward navigation served from the HTTP cache. */
function isBackForwardLoad() {
  const [entry] = /** @type {PerformanceNavigationTiming[]} */ (performance.getEntriesByType('navigation'));
  return entry?.type === 'back_forward';
}

/** @param {unknown} error */
function warn(error) {
  if (/** @type {any} */ (error)?.name !== 'AbortError') console.warn('[cart-restore-sync]', error);
}

function syncCartItems() {
  const components = document.querySelectorAll('cart-items-component[data-section-id]');

  const renders = Array.from(components, (component) => {
    if (!(component instanceof HTMLElement)) return Promise.resolve();
    const { sectionId } = component.dataset;
    if (!sectionId) return Promise.resolve();

    return sectionRenderer
      .renderSection(sectionId, {
        cache: false,
        mode: component.dataset.drawer !== undefined ? 'hydration' : 'full',
      })
      .catch(warn);
  });

  return Promise.all(renders).then(() => {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  });
}

function syncCartBubble() {
  return fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' })
    .then((response) => {
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
      return response.json();
    })
    .then((cart) => {
      const count = Number(cart?.item_count);
      if (!Number.isFinite(count)) return;

      for (const icon of document.querySelectorAll('cart-icon')) {
        /** @type {any} */ (icon).renderCartBubble?.(count, false);
      }
    })
    .catch(warn);
}

window.addEventListener('pageshow', (event) => {
  if (!event.persisted && !isBackForwardLoad()) return;

  syncCartItems();
  syncCartBubble();
});
